/**
 * Graphes du back-office.
 *
 * Trois formes, choisies d'après le travail que la donnée doit faire — et pas une de plus :
 *
 *  - `Sparkline`     : une seule série, une seule question (« l'ingestion tient-elle le rythme ? »).
 *    Pas d'axes, pas de légende : la valeur courante est affichée en chiffres à côté, le tracé
 *    ne sert qu'à montrer la FORME (plat, en escalier, effondré).
 *  - `CourbeTension` : une série de tension avec sa bande min/max et les seuils du cadran. Un
 *    seul axe des ordonnées — jamais deux échelles superposées.
 *  - `Repartition`   : des parts d'un total. Des barres empilées horizontalement plutôt qu'un
 *    camembert : comparer des angles est plus difficile que comparer des longueurs, et les
 *    quatre états du parc doivent se lire d'un coup d'œil.
 *
 * Règles tenues partout : traits fins (2 px), grille discrète, couleurs d'état réservées à
 * l'état (jamais décoratives), aucun libellé sur chaque point, et pour la courbe de tension une
 * vue tabulaire équivalente — un graphe seul n'est pas accessible.
 */

import { useMemo, useState } from 'react';
import type { CadranTension, PointTension } from '@genviz/shared/dto';
import { Couleur, EtatAppareil } from '@genviz/shared/enums';

import { LIBELLE_ETAT_APPAREIL } from '../i18n/fr';
import { formaterDecimal, formaterHeure, formaterNombre, formaterTension } from '../lib/format';
import { useTaille } from '../lib/useTaille';
import { HEX_PAR_COULEUR } from './Badges';
import { EtatVide } from './EtatVide';

/* -------------------------------------------------------------------------- */
/* Outils communs                                                              */
/* -------------------------------------------------------------------------- */

interface Echelle {
  (valeur: number): number;
}

function echelleLineaire(
  domaine: [number, number],
  etendue: [number, number],
): Echelle {
  const [d0, d1] = domaine;
  const [e0, e1] = etendue;
  const amplitude = d1 - d0 || 1;
  return (valeur: number) => e0 + ((valeur - d0) / amplitude) * (e1 - e0);
}

/** Graduations « rondes » d'un axe de valeurs. */
function graduations(min: number, max: number, nb = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [min];
  const brut = (max - min) / nb;
  const magnitude = 10 ** Math.floor(Math.log10(brut));
  const pas = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((p) => p >= brut) ?? magnitude * 10;
  const debut = Math.ceil(min / pas) * pas;
  const valeurs: number[] = [];
  for (let v = debut; v <= max + 1e-9; v += pas) valeurs.push(Number(v.toFixed(6)));
  return valeurs;
}

interface PositionInfobulle {
  x: number;
  y: number;
  contenu: JSX.Element;
}

/* -------------------------------------------------------------------------- */
/* Sparkline                                                                   */
/* -------------------------------------------------------------------------- */

export interface PointSparkline {
  t: string;
  valeur: number;
}

/**
 * Courbe compacte d'une série unique.
 * `titreAccessible` décrit la série : sans légende, c'est lui qui porte l'identité.
 */
export function Sparkline({
  points,
  hauteur = 72,
  titreAccessible,
  unite = '',
}: {
  points: readonly PointSparkline[];
  hauteur?: number;
  titreAccessible: string;
  unite?: string;
}): JSX.Element {
  const [conteneur, taille] = useTaille<HTMLDivElement>();
  const [survol, setSurvol] = useState<number | null>(null);

  const largeur = Math.max(taille.largeur, 0);
  const marge = { haut: 8, bas: 8, gauche: 0, droite: 0 };

  const rendu = useMemo(() => {
    if (largeur === 0 || points.length === 0) return null;

    const valeurs = points.map((p) => p.valeur);
    const max = Math.max(...valeurs, 1);
    const x = echelleLineaire([0, Math.max(points.length - 1, 1)], [1, largeur - 1]);
    const y = echelleLineaire([0, max], [hauteur - marge.bas, marge.haut]);

    const chemin = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.valeur)}`).join(' ');
    const aire =
      `M${x(0)},${hauteur - marge.bas} ` +
      points.map((p, i) => `L${x(i)},${y(p.valeur)}`).join(' ') +
      ` L${x(points.length - 1)},${hauteur - marge.bas} Z`;

    return { x, y, chemin, aire, max };
  }, [points, largeur, hauteur, marge.bas, marge.haut]);

  const dernier = points.length > 0 ? points[points.length - 1] : undefined;
  const indexSurvol = survol !== null && points[survol] ? survol : null;
  const pointSurvol = indexSurvol !== null ? points[indexSurvol] : undefined;

  return (
    <div className="gv-graphe" ref={conteneur}>
      {rendu && dernier ? (
        <>
          <svg
            width={largeur}
            height={hauteur}
            role="img"
            aria-label={`${titreAccessible} — dernière valeur ${formaterNombre(dernier.valeur)} ${unite}`}
            onMouseLeave={() => {
              setSurvol(null);
            }}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const ratio = (e.clientX - rect.left) / Math.max(rect.width, 1);
              const index = Math.round(ratio * (points.length - 1));
              setSurvol(Math.min(points.length - 1, Math.max(0, index)));
            }}
          >
            <path className="gv-graphe__aire" d={rendu.aire} />
            <path className="gv-graphe__trait" d={rendu.chemin} />
            {indexSurvol !== null && pointSurvol ? (
              <>
                <line
                  className="gv-graphe__viseur"
                  x1={rendu.x(indexSurvol)}
                  x2={rendu.x(indexSurvol)}
                  y1={0}
                  y2={hauteur}
                />
                <circle
                  className="gv-graphe__point"
                  cx={rendu.x(indexSurvol)}
                  cy={rendu.y(pointSurvol.valeur)}
                  r={4}
                />
              </>
            ) : (
              <circle
                className="gv-graphe__point"
                cx={rendu.x(points.length - 1)}
                cy={rendu.y(dernier.valeur)}
                r={4}
              />
            )}
          </svg>

          {indexSurvol !== null && pointSurvol ? (
            <div
              className="gv-infobulle"
              style={{ left: rendu.x(indexSurvol), top: rendu.y(pointSurvol.valeur) }}
            >
              <b>{formaterNombre(pointSurvol.valeur)}</b> {unite}
              <br />
              {formaterHeure(pointSurvol.t)}
            </div>
          ) : null}

          {/* BA_00005 — « on n'a pas les valeurs en abscisse ». L'infobulle ne se découvre qu'à
              la souris ; l'axe, lui, se lit d'un coup d'œil : début, milieu et fin de la
              fenêtre, et le maximum atteint, qui donne l'échelle verticale. */}
          <div
            className="gv-corps-s gv-secondaire"
            style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}
          >
            <span>{formaterHeure(points[0]!.t)}</span>
            <span>{formaterHeure(points[Math.floor((points.length - 1) / 2)]!.t)}</span>
            <span>{formaterHeure(dernier.t)}</span>
          </div>
          <div className="gv-corps-s gv-secondaire" style={{ textAlign: 'right' }}>
            maximum {formaterNombre(rendu.max)} {unite}
          </div>
        </>
      ) : (
        <div style={{ height: hauteur }} />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Courbe de tension                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Historique de tension d'un appareil, avec la bande min/max de chaque point agrégé et les
 * deux seuils du cadran (gardiennage et haute-orange). Ces seuils sont la raison d'être du
 * graphe : une courbe de tension sans son seuil ne dit pas si la clôture protège.
 */
export function CourbeTension({
  points,
  reperes,
  hauteur = 260,
}: {
  points: readonly PointTension[];
  reperes: CadranTension;
  hauteur?: number;
}): JSX.Element {
  const [conteneur, taille] = useTaille<HTMLDivElement>();
  const [survol, setSurvol] = useState<number | null>(null);
  const [tableau, setTableau] = useState(false);

  const largeur = Math.max(taille.largeur, 0);
  const marge = { haut: 12, bas: 26, gauche: 46, droite: 12 };

  const rendu = useMemo(() => {
    if (largeur === 0 || points.length === 0) return null;

    const t0 = new Date(points[0]?.t ?? '').getTime();
    const t1 = new Date(points[points.length - 1]?.t ?? '').getTime();

    const maxDonnees = Math.max(...points.map((p) => p.tension_max_kv), reperes.gardiennage_kv);
    const yMax = Math.min(reperes.max_kv, Math.ceil(Math.max(maxDonnees, 1) * 1.1));
    const yMin = 0;

    const x = echelleLineaire(
      [t0, t1 === t0 ? t0 + 1 : t1],
      [marge.gauche, largeur - marge.droite],
    );
    const y = echelleLineaire([yMin, yMax], [hauteur - marge.bas, marge.haut]);

    const xDe = (p: PointTension): number => x(new Date(p.t).getTime());

    const trait = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${xDe(p)},${y(p.tension_moy_kv)}`).join(' ');

    const haut = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${xDe(p)},${y(p.tension_max_kv)}`).join(' ');
    const bas = [...points]
      .reverse()
      .map((p) => `L${xDe(p)},${y(p.tension_min_kv)}`)
      .join(' ');
    const bande = `${haut} ${bas} Z`;

    return {
      x,
      y,
      xDe,
      trait,
      bande,
      graduationsY: graduations(yMin, yMax, 4),
      graduationsX: points.filter((_, i) => i % Math.ceil(points.length / 5) === 0),
    };
  }, [points, reperes, largeur, hauteur, marge.bas, marge.droite, marge.gauche, marge.haut]);

  if (points.length === 0) {
    return (
      <EtatVide
        titre="Aucune mesure sur la période"
        description="L’appareil n’a remonté aucune tension exploitable dans la fenêtre demandée."
      />
    );
  }

  const pointSurvol = survol !== null ? points[survol] : undefined;
  const infobulle: PositionInfobulle | null =
    rendu && pointSurvol
      ? {
          x: rendu.xDe(pointSurvol),
          y: rendu.y(pointSurvol.tension_moy_kv),
          contenu: (
            <>
              <b>{formaterTension(pointSurvol.tension_moy_kv)}</b>
              <br />
              min {formaterDecimal(pointSurvol.tension_min_kv)} · max{' '}
              {formaterDecimal(pointSurvol.tension_max_kv)} kV
              <br />
              {formaterHeure(pointSurvol.t)} · {pointSurvol.nb_points} mesure
              {pointSurvol.nb_points > 1 ? 's' : ''}
            </>
          ),
        }
      : null;

  return (
    <div className="gv-pile gv-pile--serree">
      <div className="gv-graphe" ref={conteneur}>
        {rendu ? (
          <>
            <svg
              width={largeur}
              height={hauteur}
              role="img"
              aria-label={`Tension d’impulsion, seuil de gardiennage ${formaterTension(reperes.gardiennage_kv)}`}
              onMouseLeave={() => {
                setSurvol(null);
              }}
              onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                let meilleur = 0;
                let distance = Number.POSITIVE_INFINITY;
                points.forEach((p, i) => {
                  const d = Math.abs(rendu.xDe(p) - x);
                  if (d < distance) {
                    distance = d;
                    meilleur = i;
                  }
                });
                setSurvol(meilleur);
              }}
            >
              <g className="gv-graphe__grille">
                {rendu.graduationsY.map((v) => (
                  <line
                    key={v}
                    x1={marge.gauche}
                    x2={largeur - marge.droite}
                    y1={rendu.y(v)}
                    y2={rendu.y(v)}
                  />
                ))}
              </g>

              {rendu.graduationsY.map((v) => (
                <text
                  key={v}
                  className="gv-graphe__axe-texte"
                  x={marge.gauche - 8}
                  y={rendu.y(v) + 4}
                  textAnchor="end"
                >
                  {formaterDecimal(v, v < 10 ? 1 : 0)}
                </text>
              ))}

              {rendu.graduationsX.map((p) => (
                <text
                  key={p.t}
                  className="gv-graphe__axe-texte"
                  x={rendu.xDe(p)}
                  y={hauteur - 8}
                  textAnchor="middle"
                >
                  {formaterHeure(p.t)}
                </text>
              ))}

              <path className="gv-graphe__bande" d={rendu.bande} />
              <path className="gv-graphe__trait" d={rendu.trait} />

              {/* Seuils : la ligne rouge est le gardiennage, l'orange la limite haute de la
                  zone d'attention. Les deux sont directement étiquetées. */}
              <line
                className="gv-graphe__seuil gv-graphe__seuil--gardiennage"
                x1={marge.gauche}
                x2={largeur - marge.droite}
                y1={rendu.y(reperes.gardiennage_kv)}
                y2={rendu.y(reperes.gardiennage_kv)}
              />
              <text
                className="gv-graphe__axe-texte"
                x={largeur - marge.droite}
                y={rendu.y(reperes.gardiennage_kv) - 5}
                textAnchor="end"
                fill="var(--gv-rouge)"
              >
                gardiennage {formaterDecimal(reperes.gardiennage_kv)} kV
              </text>

              <line
                className="gv-graphe__seuil gv-graphe__seuil--orange"
                x1={marge.gauche}
                x2={largeur - marge.droite}
                y1={rendu.y(reperes.haute_orange_kv)}
                y2={rendu.y(reperes.haute_orange_kv)}
              />
              <text
                className="gv-graphe__axe-texte"
                x={largeur - marge.droite}
                y={rendu.y(reperes.haute_orange_kv) - 5}
                textAnchor="end"
                fill="var(--gv-orange)"
              >
                zone orange {formaterDecimal(reperes.haute_orange_kv)} kV
              </text>

              {pointSurvol ? (
                <>
                  <line
                    className="gv-graphe__viseur"
                    x1={rendu.xDe(pointSurvol)}
                    x2={rendu.xDe(pointSurvol)}
                    y1={marge.haut}
                    y2={hauteur - marge.bas}
                  />
                  <circle
                    className="gv-graphe__point"
                    cx={rendu.xDe(pointSurvol)}
                    cy={rendu.y(pointSurvol.tension_moy_kv)}
                    r={4.5}
                  />
                </>
              ) : null}
            </svg>

            {infobulle ? (
              <div className="gv-infobulle" style={{ left: infobulle.x, top: infobulle.y }}>
                {infobulle.contenu}
              </div>
            ) : null}
          </>
        ) : (
          <div style={{ height: hauteur }} />
        )}
      </div>

      <div className="gv-ligne">
        <button
          type="button"
          className="gv-bouton gv-bouton--discret gv-bouton--petit"
          aria-expanded={tableau}
          onClick={() => {
            setTableau((v) => !v);
          }}
        >
          {tableau ? 'Masquer les données' : 'Voir les données'}
        </button>
        <span className="gv-corps-s gv-secondaire">
          {formaterNombre(points.length)} point{points.length > 1 ? 's' : ''}
        </span>
      </div>

      {tableau ? (
        <div className="gv-table-cadre" style={{ maxHeight: 280, overflowY: 'auto' }}>
          <table className="gv-table">
            <thead>
              <tr>
                <th>Horodatage</th>
                <th className="gv-table__num">Moyenne</th>
                <th className="gv-table__num">Min</th>
                <th className="gv-table__num">Max</th>
                <th className="gv-table__num">Mesures</th>
              </tr>
            </thead>
            <tbody>
              {points.map((p) => (
                <tr key={p.t}>
                  <td className="gv-mono">{p.t}</td>
                  <td className="gv-table__num">{formaterTension(p.tension_moy_kv)}</td>
                  <td className="gv-table__num">{formaterTension(p.tension_min_kv)}</td>
                  <td className="gv-table__num">{formaterTension(p.tension_max_kv)}</td>
                  <td className="gv-table__num">{p.nb_points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Répartition                                                                 */
/* -------------------------------------------------------------------------- */

export interface PartRepartition {
  cle: string;
  libelle: string;
  valeur: number;
  couleur: string;
}

/** Barres horizontales étiquetées : libellé, longueur, valeur. */
export function Repartition({ parts }: { parts: readonly PartRepartition[] }): JSX.Element {
  const total = parts.reduce((s, p) => s + p.valeur, 0);

  return (
    <div className="gv-repartition">
      {parts.map((p) => {
        const pourcentage = total === 0 ? 0 : (p.valeur / total) * 100;
        return (
          <div className="gv-repartition__ligne" key={p.cle}>
            <span className="gv-ligne" style={{ gap: 'var(--gv-esp-2)' }}>
              <span
                className="gv-badge__point"
                style={{ background: p.couleur }}
                aria-hidden="true"
              />
              {p.libelle}
            </span>
            <span
              className="gv-repartition__piste"
              role="img"
              aria-label={`${p.libelle} : ${formaterNombre(p.valeur)} sur ${formaterNombre(total)}`}
            >
              <span
                className="gv-repartition__barre"
                style={{ width: `${pourcentage}%`, background: p.couleur }}
              />
            </span>
            <span className="gv-nb" style={{ textAlign: 'right' }}>
              {formaterNombre(p.valeur)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Répartition du parc par état métier, dans l'ordre de gravité croissante. */
export function RepartitionParEtat({
  parEtat,
}: {
  parEtat: Record<EtatAppareil, number>;
}): JSX.Element {
  const ordre: EtatAppareil[] = [
    EtatAppareil.NOMINAL,
    EtatAppareil.ATTENTION,
    EtatAppareil.CRITIQUE,
    EtatAppareil.ETEINT,
  ];
  const couleurs: Record<EtatAppareil, string> = {
    [EtatAppareil.NOMINAL]: HEX_PAR_COULEUR[Couleur.VERT],
    [EtatAppareil.ATTENTION]: HEX_PAR_COULEUR[Couleur.ORANGE],
    [EtatAppareil.CRITIQUE]: HEX_PAR_COULEUR[Couleur.ROUGE],
    // Charte du 23/09 (FR_00053) : l'éteint volontaire est BLEU, le noir n'est plus utilisé.
    [EtatAppareil.ETEINT]: HEX_PAR_COULEUR[Couleur.BLEU],
  };

  return (
    <Repartition
      parts={ordre.map((etat) => ({
        cle: etat,
        libelle: LIBELLE_ETAT_APPAREIL[etat],
        valeur: parEtat[etat] ?? 0,
        couleur: couleurs[etat],
      }))}
    />
  );
}
